import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.addEventListener('cart:updated', this.#handleGenericCartUpdate);
    document.addEventListener('cart:add', this.#handleGenericCartUpdate);
    document.addEventListener('cart:change', this.#handleGenericCartUpdate);
    
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.removeEventListener('cart:updated', this.#handleGenericCartUpdate);
    document.removeEventListener('cart:add', this.#handleGenericCartUpdate);
    document.removeEventListener('cart:change', this.#handleGenericCartUpdate);
    
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  #handleGenericCartUpdate = (event) => {
    this.#processUpdate(event?.detail?.sections);
  };

  #handleDrawerOpen = () => {
    this.#updateStickyState();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  #handleCartLinesUpdate = (event) => {
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    const performUpdate = (sectionsData) => {
      return this.#processUpdate(sectionsData).then(() => {
        if (this.hasAttribute('auto-open') && !this.#themeDrawer?.isOpen) {
          this.#themeDrawer?.open();
        }
      });
    };

    if (event.promise) {
      event.promise
        .then(({ detail }) => {
          if (detail?.didError) {
            this.#updateStickyState();
            return;
          }

          if (sourceModal?.open) {
            sourceModal.addEventListener('close', () => performUpdate(detail?.sections), { once: true });
          } else {
            performUpdate(detail?.sections);
          }
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
          performUpdate();
        });
    } else {
      performUpdate();
    }
  };

  async #processUpdate(passedSections) {
    // 1. If sections HTML was directly returned in the add response, use it immediately
    if (passedSections && passedSections['cart-drawer']) {
      this.#renderDrawerHTML(passedSections['cart-drawer']);
      await this.#renderIconHTML(passedSections['cart-icon-bubble']);
      return;
    }

    // 2. Otherwise fetch fresh section rendering with a slight cache-busting timestamp
    await this.#refreshCartDrawer();
  }

  async #refreshCartDrawer() {
    try {
      const cacheBuster = new Date().getTime();
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=cart-drawer,cart-icon-bubble&t=${cacheBuster}`);
      if (!response.ok) return;

      const data = await response.json();

      if (data['cart-drawer']) {
        this.#renderDrawerHTML(data['cart-drawer']);
      }

      await this.#renderIconHTML(data['cart-icon-bubble']);

      this.#updateStickyState();
    } catch (err) {
      console.error('[cart-drawer] Failed to refresh cart content:', err);
    }
  }

  #renderDrawerHTML(htmlString) {
    const parsed = new DOMParser().parseFromString(htmlString, 'text/html');
    const newInner = parsed.querySelector('cart-drawer-component') || parsed.querySelector('.cart-drawer') || parsed.body;
    if (newInner) {
      this.innerHTML = newInner.innerHTML;
    }
  }

  async #renderIconHTML(htmlString) {
    // 1. Update inner HTML if section template markup was returned
    if (htmlString) {
      const iconContainers = document.querySelectorAll('#cart-icon-bubble, .header__icon--cart');
      const parsedIcon = new DOMParser().parseFromString(htmlString, 'text/html');
      iconContainers.forEach((container) => {
        const newInner = parsedIcon.querySelector('#cart-icon-bubble') || parsedIcon.body;
        if (newInner) {
          container.innerHTML = newInner.innerHTML;
        }
      });
    }

    // 2. Fetch live item count to keep data-count and visibility accurate in real time
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      const cart = await res.json();
      
      const badges = document.querySelectorAll('[data-cart-badge]');
      badges.forEach((badge) => {
        badge.textContent = cart.item_count;
        badge.setAttribute('data-count', cart.item_count);
        if (cart.item_count === 0) {
          badge.style.setProperty('display', 'none', 'important');
        } else {
          badge.style.removeProperty('display');
        }
      });
    } catch (e) {
      console.error('[cart-drawer] Error syncing cart badge count:', e);
    }
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}